import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  CarFront,
  CheckCircle2,
  Clock3,
  Handshake,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/contexts/SiteThemeContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { SITES } from "@/config/sites";
import { setSeo, setJsonLd } from "@/lib/seo";
import ProcessSteps from "@/components/site/ProcessSteps";
import { ApplyCtaPanel } from "@/components/site/ApplyCta";
import { api } from "@/lib/api";
import { HERO_IMAGE } from "@/config/imageSets";
import OptimizedImage from "@/components/site/OptimizedImage";

const SeoCrossLinks = React.lazy(() => import("@/components/site/SeoCrossLinks"));

// Existing anonymized examples. These are framed as illustrations, not reviews.
const STORIES = [
  {
    slug: "refus-desjardins",
    city: "Laval",
    titleFr: "Refus bancaire, nouvelle option trouvée",
    titleEn: "Bank refusal, new option found",
    storyFr: "Un dossier stable avait été refusé à la banque. Une analyse plus complète a permis d'identifier une solution adaptée et un véhicule d'occasion compatible avec le budget.",
    storyEn: "A stable file had been refused at the bank. A fuller review identified a suitable solution and a used vehicle compatible with the budget.",
  },
  {
    slug: "nouveau-depart",
    city: "Trois-Rivières",
    titleFr: "Un nouveau départ après une faillite",
    titleEn: "A fresh start after bankruptcy",
    storyFr: "Après une faillite, le client cherchait une solution réaliste. L'analyse de la situation a permis d'orienter le dossier vers une option de reconstruction adaptée.",
    storyEn: "After bankruptcy, the customer needed a realistic solution. Reviewing the full situation helped route the file toward a suitable rebuild option.",
  },
  {
    slug: "nouvel-arrivant",
    city: "Montréal",
    titleFr: "Nouvel arrivant, peu d'historique canadien",
    titleEn: "Newcomer with limited Canadian history",
    storyFr: "Avec un emploi stable et les bons documents, une personne nouvellement arrivée a pu explorer des options malgré l'absence d'historique de crédit au Canada.",
    storyEn: "With stable employment and the right documents, a newcomer was able to explore options despite having no Canadian credit history.",
  },
];

const SITUATION_CARDS = [
  {
    slug: "refus-bancaire",
    fr: "Refus bancaire",
    en: "Bank refusal",
    descFr: "Votre banque vous a dit non? D'autres options peuvent exister.",
    descEn: "Your bank said no? Other options may exist.",
  },
  {
    slug: "proposition-consommateur",
    fr: "Proposition de consommateur",
    en: "Consumer proposal",
    descFr: "Votre situation financière a changé. Votre projet automobile peut quand même mériter une analyse.",
    descEn: "Your financial situation changed. Your vehicle project may still deserve a review.",
  },
  {
    slug: "faillite",
    fr: "Faillite",
    en: "Bankruptcy",
    descFr: "Reprendre la route peut faire partie de votre nouveau départ.",
    descEn: "Getting back on the road can be part of your fresh start.",
  },
  {
    slug: "nouveau-canada",
    fr: "Nouvel arrivant",
    en: "Newcomer",
    descFr: "Peu ou pas d'historique de crédit au Canada? Découvrez les options qui pourraient s'offrir à vous.",
    descEn: "Little or no Canadian credit history? Discover the options that may be available to you.",
  },
  {
    slug: "travailleur-autonome",
    fr: "Travailleur autonome",
    en: "Self-employed",
    descFr: "Votre revenu ne rentre pas toujours dans les cases traditionnelles.",
    descEn: "Your income does not always fit traditional boxes.",
  },
  {
    slug: "mauvais-credit",
    fr: "Crédit difficile",
    en: "Credit challenges",
    descFr: "Retards, R9 ou autres difficultés? Commencez par nous expliquer votre situation.",
    descEn: "Late payments, R9 or other challenges? Start by telling us your situation.",
  },
];

const HOME_FAQS = {
  fr: [
    ["Est-ce que faire une demande garantit mon approbation?", "Non. Chaque situation est évaluée individuellement selon les informations fournies, le budget, le véhicule et les critères des partenaires financiers."],
    ["Puis-je faire une demande après une faillite?", "Oui. Une faillite ne signifie pas automatiquement que le financement est impossible. Nous commençons par comprendre votre situation avant d'explorer les options."],
    ["Et si je suis en proposition de consommateur?", "Vous pouvez nous parler de votre situation. Certaines solutions peuvent être analysées selon votre dossier, votre revenu et les conditions disponibles."],
    ["Ai-je besoin d'une mise de fonds?", "Pas nécessairement. Une mise de fonds peut parfois améliorer les options, mais elle n'est pas présentée comme une exigence pour commencer."],
    ["Puis-je faire une demande si ma banque m'a refusé?", "Oui. Un refus bancaire ne veut pas toujours dire qu'aucune autre solution n'existe. Nous pouvons examiner votre situation sous un autre angle."],
    ["Je suis nouvel arrivant au Canada. Puis-je faire une demande?", "Oui. Peu ou pas d'historique de crédit au Canada ne vous empêche pas de nous parler de votre situation."],
    ["Dois-je déjà avoir choisi mon véhicule?", "Non. Vous pouvez commencer par vérifier vos options de financement. Notre équipe pourra ensuite vous aider à trouver un véhicule adapté à votre situation."],
    ["Combien de temps prend la demande?", "La préqualification initiale prend moins de 2 minutes dans la plupart des cas."],
    ["Est-ce confidentiel?", "Oui. Vos informations sont traitées de façon confidentielle et utilisées pour analyser votre demande et vous accompagner dans les prochaines étapes."],
  ],
  en: [
    ["Does applying guarantee my approval?", "No. Every situation is reviewed individually based on the information provided, budget, vehicle, and financing partner criteria."],
    ["Can I apply after bankruptcy?", "Yes. Bankruptcy does not automatically make financing impossible. We start by understanding your situation before exploring options."],
    ["What if I am in a consumer proposal?", "You can tell us about your situation. Some solutions may be reviewed based on your file, income, and available conditions."],
    ["Do I need a down payment?", "Not necessarily. A down payment can sometimes improve options, but it is not presented as a requirement to start."],
    ["Can I apply if my bank declined me?", "Yes. A bank refusal does not always mean no other solution exists. We can look at your situation from another angle."],
    ["I am new to Canada. Can I apply?", "Yes. Little or no Canadian credit history does not prevent you from telling us about your situation."],
    ["Do I need to have chosen my vehicle?", "No. You can start by checking your financing options. Our team can then help you find a vehicle suited to your situation."],
    ["How long does the application take?", "The initial prequalification usually takes under 2 minutes."],
    ["Is it confidential?", "Yes. Your information is handled confidentially and used to review your request and support the next steps."],
  ],
};

function SectionHeader({ eyebrow, title, subtitle, site, center = false }) {
  return (
    <div className={center ? "mx-auto mb-8 max-w-2xl text-center" : "mb-8 max-w-2xl"}>
      <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: site.theme.primary }}>{eyebrow}</div>
      <h2 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl" style={{ color: site.theme.text }}>{title}</h2>
      {subtitle ? <p className="mt-3 text-base leading-relaxed" style={{ color: site.theme.muted }}>{subtitle}</p> : null}
    </div>
  );
}

export default function MarketplaceHome() {
  const site = useSite();
  const { lang } = useLanguage();
  const { siteKey } = useParams();
  const [liveCount, setLiveCount] = useState(null);
  const isFr = lang === "fr";
  const faqs = HOME_FAQS[lang] || HOME_FAQS.fr;

  useEffect(() => {
    let cancelled = false;
    api.get("/social-proof").then(({ data }) => {
      if (!cancelled) setLiveCount(data?.approvals_this_week ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const title = isFr
      ? "RétablirMonCrédit — Financement auto adapté à votre situation"
      : "RétablirMonCrédit — Auto financing adapted to your situation";
    const desc = isFr
      ? "Vérifiez vos options de financement automobile au Québec en moins de 2 minutes. Refus bancaire, faillite, proposition de consommateur, nouvel arrivant ou crédit difficile."
      : "Check your Quebec auto financing options in under 2 minutes. Bank refusal, bankruptcy, consumer proposal, newcomer, or credit challenges.";
    setSeo({ title, description: desc, canonicalPath: `/${siteKey}`, lang, ogType: "website" });
    setJsonLd("organization", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Rétablirmoncredit.com",
      url: "https://retablirmoncredit.com",
      logo: SITES[siteKey]?.logo,
      telephone: SITES[siteKey]?.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "1007 Boul René-Lévesque",
        addressLocality: "Verdun",
        addressRegion: "QC",
        postalCode: "H3E 0H1",
        addressCountry: "CA",
      },
      areaServed: { "@type": "State", name: "Québec" },
      slogan: isFr ? "Toujours là pour vous tendre la main" : "Always here to lend a hand",
    });
    setJsonLd("home-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map(([question, answer]) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: { "@type": "Answer", text: answer },
      })),
    });
  }, [faqs, isFr, lang, siteKey]);

  const trustItems = [
    { icon: Clock3, label: isFr ? "Demande en moins de 2 minutes" : "Application in under 2 minutes" },
    { icon: ShieldCheck, label: isFr ? "100 % confidentiel" : "100% confidential" },
    { icon: MapPin, label: isFr ? "Service partout au Québec" : "Service across Quebec" },
    { icon: Users, label: isFr ? "8 prêteurs partenaires" : "8 partner lenders" },
  ];

  const whyItems = [
    {
      icon: Handshake,
      title: isFr ? "Une approche adaptée à votre situation" : "An approach adapted to your situation",
      body: isFr ? "Nous commençons par comprendre votre réalité avant de chercher une solution." : "We start by understanding your reality before looking for a solution.",
    },
    {
      icon: Search,
      title: isFr ? "Plusieurs situations de crédit" : "Many credit situations",
      body: isFr ? "Refus bancaire, faillite, proposition, crédit difficile, nouvel arrivant et plus." : "Bank refusal, bankruptcy, proposal, credit challenges, newcomer and more.",
    },
    {
      icon: CarFront,
      title: isFr ? "Financement avant le véhicule" : "Financing before the vehicle",
      body: isFr ? "Commencez par explorer vos options. Nous vous aiderons ensuite à trouver un véhicule approprié." : "Start by exploring your options. We then help you find an appropriate vehicle.",
    },
    {
      icon: Clock3,
      title: isFr ? "Simple et rapide" : "Simple and fast",
      body: isFr ? "Une demande courte pour commencer, sans formulaire interminable." : "A short application to start, without an endless form.",
    },
    {
      icon: MapPin,
      title: isFr ? "Partout au Québec" : "Across Quebec",
      body: isFr ? "Un accompagnement accessible où que vous soyez au Québec." : "Accessible support wherever you are in Quebec.",
    },
  ];

  return (
    <div data-testid="marketplace-home" style={{ backgroundColor: site.theme.background }}>
      {/* 1. Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(circle at 15% 20%, ${site.theme.primary}20, transparent 50%), radial-gradient(circle at 90% 90%, ${site.theme.accentWarm}16, transparent 55%)` }}
          aria-hidden
        />
        <div className="relative mx-auto max-w-6xl px-4 pb-12 pt-12 sm:px-6 sm:pt-20 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
            <div>
              <span
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em]"
                style={{ borderColor: `${site.theme.primary}55`, color: site.theme.primary, backgroundColor: `${site.theme.primary}12` }}
              >
                <Sparkles className="h-3 w-3" /> {isFr ? "Financement auto Québec" : "Quebec auto financing"}
              </span>
              <h1 className="mt-5 max-w-3xl text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl" style={{ color: site.theme.text }} data-testid="hero-title">
                {isFr ? "On commence par votre situation, pas par votre cote de crédit." : "We start with your situation, not your credit score."}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed sm:text-lg" style={{ color: site.theme.muted }} data-testid="hero-subtitle">
                {isFr
                  ? "Refus bancaire, crédit difficile, proposition de consommateur, faillite ou nouvel arrivant? RétablirMonCrédit vous aide à explorer vos options de financement automobile et à trouver un véhicule adapté à votre situation et à votre budget."
                  : "Bank refusal, credit challenges, consumer proposal, bankruptcy or newcomer? RétablirMonCrédit helps you explore auto financing options and find a vehicle suited to your situation and budget."}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to={`/${siteKey}/apply`}>
                  <Button
                    size="lg"
                    className="font-bold uppercase tracking-wider text-white shadow-lg transition hover:-translate-y-0.5"
                    style={{ backgroundColor: site.theme.accentWarm, borderRadius: site.theme.radius, boxShadow: `0 12px 30px -8px ${site.theme.accentWarm}66` }}
                    data-testid="hero-cta-primary"
                  >
                    {isFr ? "Vérifier mon admissibilité" : "Check my eligibility"} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button size="lg" variant="outline" style={{ borderColor: site.theme.primary, color: site.theme.primary, borderRadius: site.theme.radius }} data-testid="hero-cta-secondary">
                    {isFr ? "Comment ça fonctionne" : "How it works"}
                  </Button>
                </a>
              </div>
              <p className="mt-3 text-xs font-semibold" style={{ color: site.theme.muted }} data-testid="hero-cta-support">
                {isFr ? "Rapide • Sans obligation • 100 % confidentiel" : "Fast • No obligation • 100% confidential"}
              </p>
            </div>

            <div className="relative">
              <div className="relative overflow-hidden" style={{ borderRadius: site.theme.radius, boxShadow: site.theme.shadowLg }}>
                <OptimizedImage
                  {...HERO_IMAGE}
                  alt={isFr ? "Client qui reprend la route" : "Customer getting back on the road"}
                  className="h-[340px] w-full object-cover sm:h-[430px]"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-4 left-5 hidden max-w-[280px] rounded-2xl border bg-white px-4 py-3 shadow-lg sm:block" style={{ borderColor: site.theme.border }}>
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>
                  {isFr ? "Notre promesse" : "Our promise"}
                </div>
                <div className="mt-1 text-sm font-black leading-snug" style={{ color: site.theme.text }}>
                  {isFr ? "Toujours là pour vous tendre la main" : "Always here to lend a hand"}
                </div>
              </div>
              <div className="absolute -left-4 top-6 hidden max-w-[210px] rounded-2xl border bg-white p-4 shadow-lg sm:block" style={{ borderColor: site.theme.border }} data-testid="hero-live-stat">
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: site.theme.primary }}>{isFr ? "Cette semaine" : "This week"}</div>
                <div className="mt-1 text-2xl font-black" style={{ color: site.theme.text }}>{liveCount ?? "—"}</div>
                <div className="text-xs" style={{ color: site.theme.muted }}>{isFr ? "dossiers analysés" : "files reviewed"}</div>
              </div>
            </div>
          </div>

          {/* 2. Trust/reassurance */}
          <div className="mt-12 grid grid-cols-2 gap-2.5 sm:grid-cols-4" data-testid="home-trust-strip">
            {trustItems.map((item) => (
              <div key={item.label} className="flex items-center gap-2 border px-3 py-2.5" style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}>
                <item.icon className="h-4 w-4 shrink-0" style={{ color: site.theme.primary }} />
                <span className="text-xs font-semibold" style={{ color: site.theme.text }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. How it works */}
      <ProcessSteps id="how-it-works" />

      {/* 4. Customer situations */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-situations">
        <SectionHeader
          site={site}
          eyebrow={isFr ? "Votre situation" : "Your situation"}
          title={isFr ? "Une solution adaptée à votre situation" : "A solution adapted to your situation"}
          subtitle={isFr ? "Choisissez ce qui ressemble le plus à votre réalité. Chaque page mène au même questionnaire simple." : "Choose what best matches your reality. Every page leads to the same simple questionnaire."}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SITUATION_CARDS.map((card) => (
            <Link
              key={card.slug}
              to={`/${siteKey}/${card.slug}`}
              className="group border p-5 transition hover:-translate-y-1 hover:shadow-lg"
              style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}
              data-testid={`home-situation-${card.slug}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black" style={{ color: site.theme.text }}>{isFr ? card.fr : card.en}</h3>
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" style={{ color: site.theme.primary }} />
              </div>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: site.theme.muted }}>{isFr ? card.descFr : card.descEn}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* 5. Financing first */}
      <section className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="financing-first">
        <div className="grid items-center gap-8 border p-6 sm:p-8 lg:grid-cols-[1fr_0.8fr]" style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: site.theme.primary }}>
              {isFr ? "Financement d'abord" : "Financing first"}
            </div>
            <h2 className="mt-2 text-3xl font-black tracking-tight sm:text-4xl" style={{ color: site.theme.text }}>
              {isFr ? "Pas encore choisi votre véhicule? Aucun problème." : "Haven't chosen your vehicle yet? No problem."}
            </h2>
            <p className="mt-4 text-base leading-relaxed" style={{ color: site.theme.muted }}>
              {isFr
                ? "Commencez par vérifier vos options de financement. Une fois votre situation mieux comprise, nous pourrons vous aider à trouver un véhicule adapté à votre budget et aux conditions de financement disponibles."
                : "Start by checking your financing options. Once your situation is better understood, we can help you find a vehicle suited to your budget and available financing conditions."}
            </p>
            <Link to={`/${siteKey}/apply`} className="mt-6 inline-block">
              <Button size="lg" className="font-bold uppercase tracking-wider text-white" style={{ backgroundColor: site.theme.primary, borderRadius: site.theme.radius }} data-testid="financing-first-cta">
                {isFr ? "Voir mes options" : "See my options"} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid gap-3">
            {[
              isFr ? "Vous n'avez pas besoin de trouver le véhicule avant de commencer." : "You do not need to find the vehicle before starting.",
              isFr ? "Nous clarifions d'abord les options de financement." : "We clarify financing options first.",
              isFr ? "Ensuite, nous vous aidons à trouver un véhicule compatible." : "Then we help you find a compatible vehicle.",
            ].map((text) => (
              <div key={text} className="flex items-start gap-3 border p-4" style={{ backgroundColor: site.theme.background, borderColor: site.theme.border, borderRadius: site.theme.radius }}>
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: site.theme.primary }} />
                <span className="text-sm font-semibold leading-relaxed" style={{ color: site.theme.text }}>{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Why RMC */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8" data-testid="why-rmc">
        <SectionHeader
          site={site}
          center
          eyebrow={isFr ? "Pourquoi nous" : "Why us"}
          title={isFr ? "Pourquoi RétablirMonCrédit?" : "Why RétablirMonCrédit?"}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {whyItems.map((item) => (
            <div key={item.title} className="border p-5" style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}>
              <item.icon className="h-5 w-5" style={{ color: site.theme.primary }} />
              <h3 className="mt-4 text-base font-black leading-tight" style={{ color: site.theme.text }}>{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: site.theme.muted }}>{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <ApplyCtaPanel placement="home-mid" compact className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" />

      {/* 7. Examples */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-success-stories">
        <SectionHeader
          site={site}
          eyebrow={isFr ? "Exemples de situations" : "Situation examples"}
          title={isFr ? "Chaque dossier commence par une écoute réelle" : "Every file starts with real listening"}
          subtitle={isFr ? "Exemples anonymisés et illustratifs — pas des témoignages ni une promesse d'approbation." : "Anonymized illustrative examples — not testimonials or an approval promise."}
        />
        <div className="grid gap-4 md:grid-cols-3">
          {STORIES.map((story) => (
            <article key={story.slug} className="border p-5" style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }} data-testid={`home-story-${story.slug}`}>
              <div className="flex items-center justify-between gap-3">
                <span className="rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest" style={{ backgroundColor: `${site.theme.accentWarm}16`, color: site.theme.accentWarm }}>
                  {isFr ? "Exemple illustratif" : "Illustrative example"}
                </span>
                <span className="text-xs font-semibold" style={{ color: site.theme.muted }}>{story.city}</span>
              </div>
              <h3 className="mt-4 text-lg font-black leading-tight" style={{ color: site.theme.text }}>{isFr ? story.titleFr : story.titleEn}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: site.theme.muted }}>{isFr ? story.storyFr : story.storyEn}</p>
            </article>
          ))}
        </div>
      </section>

      {/* 8. Educational support */}
      <section className="mx-auto max-w-6xl px-4 pb-8 sm:px-6 lg:px-8" data-testid="home-education">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { to: `/${siteKey}/outils`, title: isFr ? "Calculatrices" : "Calculators", body: isFr ? "Estimez vos paiements et votre budget avant de commencer." : "Estimate payments and budget before starting.", icon: Clock3 },
            { to: `/${siteKey}/guides`, title: isFr ? "Guides pratiques" : "Practical guides", body: isFr ? "Comprenez les options sans jargon inutile." : "Understand the options without unnecessary jargon.", icon: Search },
            { to: `/${siteKey}/mon-plan`, title: isFr ? "Mon plan de crédit" : "My credit plan", body: isFr ? "Voyez les prochaines étapes possibles pour votre nouveau départ." : "See possible next steps for your fresh start.", icon: BadgeCheck },
          ].map((item) => (
            <Link key={item.to} to={item.to} className="group border p-5 transition hover:-translate-y-1" style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}>
              <item.icon className="h-5 w-5" style={{ color: site.theme.primary }} />
              <h3 className="mt-4 text-lg font-black" style={{ color: site.theme.text }}>{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: site.theme.muted }}>{item.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold" style={{ color: site.theme.primary }}>
                {isFr ? "Explorer" : "Explore"} <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <React.Suspense fallback={null}>
        <SeoCrossLinks />
      </React.Suspense>

      {/* 9. FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-faq">
        <SectionHeader
          site={site}
          center
          eyebrow={isFr ? "Questions fréquentes" : "Frequently asked questions"}
          title={isFr ? "Vos questions avant de commencer" : "Your questions before starting"}
        />
        <div className="border px-5" style={{ backgroundColor: site.theme.surface, borderColor: site.theme.border, borderRadius: site.theme.radius }}>
          <Accordion type="single" collapsible>
            {faqs.map(([question, answer], index) => (
              <AccordionItem key={question} value={`home-faq-${index}`}>
                <AccordionTrigger data-testid={`home-faq-question-${index}`} className="text-left font-bold" style={{ color: site.theme.text }}>
                  {question}
                </AccordionTrigger>
                <AccordionContent className="leading-relaxed" style={{ color: site.theme.muted }}>
                  {answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
